import React from 'react';
import ReactTable from 'react-table';
import { useSelector } from 'react-redux';
import ReactLoading from 'react-loading';
import { Typography, Grid, Box, Tooltip } from '@mui/material';

import { Link } from 'react-router-dom';
import DisputeActions from './DisputeActions';
import DisputeCheckbox from './DisputeCheckbox';
import MessageButton from './MessageButton';
import CheckboxHeader from './CheckboxHeader';

import { cfPath } from '../../../utilities/UrlPaths';

const DisputesTable = ({ selectedDispute }) => {
  const { houseRef } = useSelector(store => store.house) || {};
  const disputes = useSelector(store => store.disputes.disputes) || [];
  const fetchDisputesLoading = useSelector(store => store.disputes.fetchDisputesLoading) || false;

  const disputeColumns = [
    {
      Header: <CheckboxHeader disputes={disputes} />,
      headerClassName: 'checkbox-header',
      width: 50,
      id: 'checkbox',
      sortable: false,
      Cell: ({ original: { disputeInfo } }) => (
        <DisputeCheckbox
          disputeID={disputeInfo.disputeID}
          openDispute={disputeInfo.openDispute}
          disputes={disputes}
        />
      )
    },
    {
      Header: 'Open Date',
      id: 'openDate',
      width: 110,
      sortable: true,
      Cell: ({ original: { disputeInfo } }) => (
        <Tooltip title={disputeInfo.openDate}>
          <span id={disputeInfo.openDate}>{disputeInfo.openDate}</span>
        </Tooltip>
      )
    },
    {
      Header: 'Buyer',
      accessor: 'bidderInfo.lastName',
      id: 'buyer',
      sortable: true,
      Cell: ({ original: { bidderInfo } }) => {
        const { firstName = '', lastName = '', channel = '', approvalID } = bidderInfo || {};
        const channelLabel = channel ? `(${channel})` : '';
        const label = `${lastName}, ${firstName} ${channelLabel}`;
        return (
          <Tooltip title={label}>
            <a
              className="dispute-table-data"
              href={`${cfPath()}/al/bidders/viewBidder.cfm?approvalID=${approvalID}`}
            >
              {label}
            </a>
          </Tooltip>
        );
      }
    },
    {
      Header: 'Sale',
      id: 'sale',
      accessor: 'catalogInfo.catalogTitle',
      sortable: true,
      Cell: ({ original: { catalogInfo } }) => {
        const { catalogTitle = '' } = catalogInfo || {};
        return (
          <Tooltip title={catalogTitle}>
            <span className="dispute-table-data">{catalogTitle}</span>
          </Tooltip>
        );
      }
    },
    {
      Header: 'Lot',
      id: 'lot',
      accessor: 'lotInfo.lotNumber',
      sortable: true,
      Cell: ({ original: { lotInfo } }) => {
        const { lotNumber, lotTitle, lotURL } = lotInfo || {};
        const label = `${lotNumber}: ${lotTitle}`;
        return (
          <Tooltip title={label}>
            <a className="dispute-table-data" href={lotURL} target="_blank" rel="noreferrer">
              {label}
            </a>
          </Tooltip>
        );
      }
    },
    {
      Header: 'Amount',
      id: 'amount',
      width: 80,
      sortable: false,
      Cell: ({ original: { priceInfo } }) => {
        const { currencySymbol = '', payable = '' } = priceInfo || {};
        const label = `${currencySymbol}${payable}`;
        return (
          <Tooltip title={label}>
            <span className="dispute-table-data">{label}</span>
          </Tooltip>
        );
      }
    },
    {
      Header: 'Dispute Status',
      id: 'disputeStatus',
      accessor: 'disputeInfo.disputeStat',
      sortable: true,
      Cell: ({ original: { disputeInfo } }) => {
        const { disputeStat, disputeRef } = disputeInfo || {};
        return (
          <Tooltip title={disputeStat}>
            <Link className="dispute-table-data" to={`/customers/dispute/${disputeRef}`}>
              {disputeStat}
            </Link>
          </Tooltip>
        );
      }
    },
    {
      Header: 'Message',
      id: 'message',
      width: 90,
      sortable: false,
      Cell: ({ original }) => <MessageButton disputeData={original} />
    }
  ];

  return (
    <Grid mt={3}>
      {!houseRef || fetchDisputesLoading ? (
        <Box display="flex" direction="column" alignItems="center" justifyContent="center">
          <ReactLoading type="spin" color="black" />
        </Box>
      ) : (
        <>
          {disputes.length ? (
            <Box>
              <ReactTable
                defaultSorted={[{ id: 'openDate', desc: true }]}
                data={disputes}
                columns={disputeColumns}
                pageSize={disputes.length}
                minRows={0}
                style={{ width: '100%', maxHeight: '400px' }}
                showPagination={false}
                className="-highlight dispute-table"
              />
              <DisputeActions selectedDispute={selectedDispute} />
            </Box>
          ) : (
            <Typography variant="h6" color="red">
              No Disputes Exist
            </Typography>
          )}
        </>
      )}
    </Grid>
  );
};

export default DisputesTable;
